import {Autocomplete, Box, Button, TextField, Typography, Switch} from "@mui/material";
import React from "react";
import {Link as RouterLink} from "react-router-dom";
import {DataObject} from "@mui/icons-material";
import {CustomDatePicker} from "../../../../../components/datepicker";
import {Dayjs} from "dayjs";
import {GooglePlacesAutocomplete} from "../../../../../components/address-autocomplete";
import {LoadingButtonWithPopup} from "../../../../../components/loading-button-with-popup";
import {states} from "../../../../../constants/states.constants";
import palette from "../../../../../theme/palette";
import {Warning, GppGoodOutlined, GavelOutlined, TravelExploreOutlined} from "@mui/icons-material";

const INK = '#0E1230';

// The "what you're getting" strip between the hero and the reports table
// (2026-08-28 call: Jonas wanted differentiators reinforced on every visit,
// Dan placed them "in between those two things"). Copy is a placeholder until
// Jonas sends his wording. Accuracy guardrails: the 50-state claim applies to
// the registry sweep only — court-docket depth varies by state, so no
// 50-state claim on records.
const DIFFERENTIATORS = [
    { icon: <GppGoodOutlined sx={{ fontSize: 18 }} />, label: 'Sex offender registries across all 50 states' },
    { icon: <GavelOutlined sx={{ fontSize: 18 }} />, label: 'Criminal and court records' },
    { icon: <TravelExploreOutlined sx={{ fontSize: 18 }} />, label: 'Address and area safety ratings' },
];

// Page entrance. Scoped keyframes; disabled for users who prefer reduced motion.
const fadeUpSx = (delayMs: number) => ({
    '@keyframes iraFadeUp': {
        from: { opacity: 0, transform: 'translateY(8px)' },
        to: { opacity: 1, transform: 'translateY(0)' },
    },
    animation: `iraFadeUp .4s ease ${delayMs}ms both`,
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

// Strips the outlined-input chrome so embedded controls (TextField, the date
// picker, both autocompletes) read as bare text inside a search-bar segment.
const bareInputSx = {
    width: '100%',
    '& .MuiTextField-root': { width: '100%' },
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    // Every embedded widget (plain input, date picker + icon, autocompletes)
    // gets the SAME 34px root so the three segments share one baseline.
    '& .MuiInputBase-root': { padding: 0, height: 34, minHeight: 34, alignItems: 'center' },
    '& .MuiAutocomplete-inputRoot': { padding: '0 !important' },
    '& .MuiInputBase-input': { padding: '0 !important', fontSize: 15.5, fontWeight: 500, color: INK, height: 34, lineHeight: '34px', boxSizing: 'border-box' },
    '& .MuiInputBase-input::placeholder': { color: '#98A1B3', opacity: 1, fontWeight: 400 },
    '& .MuiIconButton-root': { padding: 0.5 },
    // Zeroing the root padding above also removes the space MUI reserves for
    // the autocomplete's absolutely-positioned clear (✕) adornment — reserve
    // it back so long addresses ellipsize instead of running under the icon.
    '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': { paddingRight: '38px !important', textOverflow: 'ellipsis' },
    '& .MuiAutocomplete-endAdornment': { right: 4 },
};

interface SegmentProps {
    label: React.ReactNode;
    flex: number;
    disabled?: boolean;
    children: React.ReactNode;
}

// One slot of the unified search bar: tiny bold label over a bare input, the
// whole slot highlighting on focus.
const Segment: React.FC<SegmentProps> = ({ label, flex, disabled, children }) => (
    <Box
        sx={{
            flex: { xs: 'unset', md: `${flex} 1 0%` },
            minWidth: 0,
            px: 3,
            py: 1.5,
            height: { md: 76 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            borderRadius: { xs: '18px', md: '999px' },
            transition: 'background-color .15s ease',
            '&:focus-within': { backgroundColor: '#F3F7FB' },
            ...(disabled ? { opacity: 0.45, pointerEvents: 'none' } : {}),
        }}
    >
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: INK, lineHeight: '16px', height: 16, mb: 0.5 }}>
            {label}
        </Typography>
        <Box sx={{ height: 34, display: 'flex', alignItems: 'center' }}>
            {children}
        </Box>
    </Box>
);

const SegmentDivider: React.FC = () => (
    <Box sx={{
        alignSelf: 'stretch',
        width: { xs: 'auto', md: '1px' },
        height: { xs: '1px', md: 'auto' },
        my: { xs: 0, md: 1.75 },
        mx: { xs: 3, md: 0 },
        backgroundColor: '#E9EEF4',
        flexShrink: 0,
    }} />
);

interface StateOption {
    name: string;
    abbreviation: string;
}

interface Props {
    dob: Dayjs | null;
    setDob: (date: Dayjs | null) => void;
    patientName: string;
    setPatientName: (name: string) => void;
    age: string;
    setAge: (age: string) => void;
    address: { address: string; placeId: string };
    setAddress: (address: { address: string; placeId: string }) => void;
    loading: boolean;
    handleSearch : () => void;
    onlyAddress : boolean;
    showStatewideSearch: boolean;
    showApiDocsLink: boolean;
    isAddressError: boolean;
    setIsAddressError: (flag : boolean) => void;
    selectedState: StateOption | null;
    setSelectedState: (state: StateOption | null) => void;
    isStatewide: boolean;
    setIsStatewide: (flag: boolean) => void;
}

export const RiskAssessmentSearch: React.FC<Props> = ({
                                                          dob, setDob,
                                                          patientName, setPatientName,
                                                          age, setAge,
                                                          address, setAddress,
                                                          loading,
                                                          handleSearch,
                                                          onlyAddress,
                                                          showStatewideSearch,
                                                          showApiDocsLink,
                                                          isAddressError,
                                                          setIsAddressError,
                                                          selectedState,
                                                          setSelectedState,
                                                          isStatewide,
                                                          setIsStatewide
                                                      }) => {
    const hasState = !!selectedState;
    const isPersonInfoMissing = !onlyAddress && (patientName.length === 0 || dob === null);
    const hasLocation = isStatewide ? hasState : (address.address.length > 0 || hasState);
    const isDisabled = loading || isPersonInfoMissing || !hasLocation;

    const required = <Typography component="span" sx={{ color: palette.primary.main }}>&nbsp;*</Typography>;

    return (
        <Box sx={{ maxWidth: 1240, mx: 'auto', pt: { xs: 1, md: 2 }, pb: { xs: 4, md: 6 } }}>
        <Box
            sx={{
                ...fadeUpSx(0),
                position: 'relative',
                overflow: 'hidden',
                textAlign: 'center',
                borderRadius: '28px',
                px: { xs: 3, md: 8 },
                pt: { xs: 5, md: 8 },
                pb: { xs: 5, md: 7 },
                background: `radial-gradient(1100px 480px at 82% -12%, rgba(255,255,255,0.32), transparent 62%),
                             radial-gradient(900px 420px at -10% 110%, rgba(20,47,83,0.28), transparent 60%),
                             linear-gradient(135deg, #78D1F1 0%, #11A7E2 48%, #206FA5 100%)`,
            }}
        >
            <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.22,
                backgroundImage: 'linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)',
                backgroundSize: '36px 36px',
                maskImage: 'radial-gradient(ellipse at 50% 0%, black 20%, transparent 70%)',
                WebkitMaskImage: 'radial-gradient(ellipse at 50% 0%, black 20%, transparent 70%)' }} />

            {/* Quiet door to the API page — the collapsed standalone sidebar
                makes this the primary discovery point for the docs. */}
            {showApiDocsLink && (
                <Button
                    component={RouterLink}
                    to="/api-access"
                    startIcon={<DataObject sx={{ fontSize: 16 }} />}
                    sx={{
                        position: 'absolute',
                        top: 20,
                        right: 24,
                        zIndex: 1,
                        color: '#fff',
                        borderRadius: '999px',
                        px: 2,
                        py: 0.5,
                        fontSize: 13,
                        fontWeight: 600,
                        textTransform: 'none',
                        backgroundColor: 'rgba(255,255,255,0.12)',
                        border: '1px solid rgba(255,255,255,0.28)',
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.22)' },
                    }}
                >
                    API docs
                </Button>
            )}

            <Box sx={{ position: 'relative' }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#143D63', mb: 2 }}>
                    Pre-visit safety screening
                </Typography>
                <Typography component="h1" sx={{ fontSize: { xs: 34, md: 54 }, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.08, color: '#fff', mb: 2 }}>
                    Intake Risk Assessment
                </Typography>
                <Typography sx={{ fontSize: { xs: 15, md: 18 }, color: 'rgba(255,255,255,0.92)', maxWidth: 640, mx: 'auto', mb: { xs: 4, md: 6 } }}>
                    Screen the person, the address, and the surrounding area before your team walks in.
                </Typography>
            </Box>

            {/* The search bar IS the interface: one white surface on the dark canvas. */}
            <Box
                sx={{
                    ...fadeUpSx(90),
                    position: 'relative',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: { xs: 'stretch', md: 'center' },
                    textAlign: 'left',
                    backgroundColor: '#fff',
                    borderRadius: { xs: '28px', md: '999px' },
                    boxShadow: '0 30px 80px -30px rgba(0,0,0,0.55), 0 6px 20px -10px rgba(0,0,0,0.25)',
                    p: 1,
                }}
            >
                <Segment label={<>Full Name{!onlyAddress && required}</>} flex={1.1} disabled={onlyAddress}>
                    <TextField
                        fullWidth
                        placeholder="First and Last name"
                        value={patientName}
                        onChange={(e) => setPatientName(e.target.value)}
                        disabled={onlyAddress}
                        sx={bareInputSx}
                    />
                </Segment>
                <SegmentDivider />
                <Segment label={<>Date of Birth{!onlyAddress && required}</>} flex={0.9} disabled={onlyAddress}>
                    <Box sx={bareInputSx}>
                        <CustomDatePicker customSx={{ width: '100%' }} date={dob} setDate={setDob} label={'mm/dd/yyyy'} isDisabled={onlyAddress} hideFloatingLabel />
                    </Box>
                </Segment>
                <SegmentDivider />

                {isStatewide ? (
                    <Segment label={<>State{required}</>} flex={1.4}>
                        <Autocomplete
                            fullWidth
                            options={states}
                            value={selectedState}
                            getOptionLabel={(option) => option.name}
                            onChange={(_, value) => {
                                setSelectedState(value);
                                if (value) {
                                    setIsAddressError(false);
                                }
                            }}
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Select State" sx={bareInputSx} />
                            )}
                        />
                    </Segment>
                ) : (
                    <Segment label={<>Address{required}</>} flex={1.4}>
                        <Box sx={bareInputSx}>
                            <GooglePlacesAutocomplete placeholder="US Street Address" initialValue={{description:address.address,place_id:address.placeId}} onAddressSelect={(address,placeId)=>{setIsAddressError(false); setAddress({address,placeId})}}/>
                        </Box>
                    </Segment>
                )}

                {/* LoadingButtonWithPopup hardcodes fullWidth and drops sx, so the
                    width lives on this wrapper and the look on a descendant rule. */}
                <Box
                    sx={{
                        width: { xs: 'auto', md: 170 },
                        flexShrink: 0,
                        alignSelf: { xs: 'stretch', md: 'center' },
                        m: { xs: 1, md: 0 },
                        ml: { md: 1 },
                        '& .MuiButton-root': {
                            borderRadius: '999px',
                            fontWeight: 600,
                            fontSize: 15,
                            height: 56,
                            color: '#fff',
                            transition: 'box-shadow .15s ease, background .15s ease',
                            background: isStatewide
                                ? '#AB7C5D'
                                : `linear-gradient(135deg, ${palette.primary.main} 0%, ${palette.darkBlue} 100%)`,
                            boxShadow: isStatewide
                                ? '0 6px 16px -8px rgba(171,124,93,0.5)'
                                : '0 6px 16px -8px rgba(17,167,226,0.5)',
                            '&:hover': {
                                background: isStatewide
                                    ? palette.darkBlue
                                    : `linear-gradient(135deg, ${palette.primary.main} 20%, ${palette.darkBlue} 120%)`,
                                boxShadow: isStatewide
                                    ? '0 8px 20px -8px rgba(32,111,165,0.55)'
                                    : '0 8px 20px -8px rgba(17,167,226,0.6)',
                            },
                            '&.Mui-disabled': {
                                background: '#EDF1F6',
                                color: '#9AA3B2',
                                boxShadow: 'none',
                            },
                            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                        },
                    }}
                >
                    <LoadingButtonWithPopup
                        loading={loading}
                        loadingText="Generating Risk Assessment Report"
                        onClick={handleSearch}
                        disabled={isDisabled}
                    >
                        Generate
                    </LoadingButtonWithPopup>
                </Box>
            </Box>

            {isAddressError && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#FFB4AB' }}>
                    Enter address with street number and name (e.g., "123 Main St")
                </Typography>
            )}

            <Box sx={{ ...fadeUpSx(160), position: 'relative', mt: 3.5, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', columnGap: 4, rowGap: 1.5 }}>
                {/* Statewide search is a per-customer setting (off for standalone
                    trials): reps read it as "search harder" when it really means
                    "no address available". The caption below spells out what it
                    does. POM staff can enable it on request. */}
                {showStatewideSearch && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Switch
                            size="small"
                            checked={isStatewide}
                            onChange={(e) => {
                                setIsStatewide(e.target.checked);
                                setIsAddressError(false);
                                if (e.target.checked) {
                                    setAddress({address: '', placeId: ''});
                                }
                            }}
                            sx={{
                                '& .MuiSwitch-switchBase': { color: '#B9C4D4' },
                                '& .MuiSwitch-track': { backgroundColor: 'rgba(255,255,255,0.35)' },
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: '#7FD4F5',
                                    '& + .MuiSwitch-track': {
                                        backgroundColor: '#7FD4F5',
                                        opacity: 0.5,
                                    }
                                }
                            }}
                        />
                        <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#fff', ml: 0.75 }}>Statewide search</Typography>
                    </Box>
                    <Typography sx={{ fontSize: 12.5, color: 'rgba(255,255,255,0.82)', textAlign: 'center', maxWidth: 460, lineHeight: 1.35 }}>
                        Search by name, date of birth, and state when you don't have a physical address.
                    </Typography>
                </Box>
                )}
            </Box>

            {isStatewide && (
                <Box sx={{ position: 'relative', mt: 2, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: 1, maxWidth: 720, mx: 'auto' }}>
                    <Warning sx={{ color: '#E3B693', fontSize: 16, mt: 0.25, flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ color: '#E3B693', textAlign: 'left' }}>
                        <Box component="span" sx={{ fontWeight: 700 }}>Statewide searches may return many results</Box> and take longer to process. This search will check sex offender registries across the entire selected state. Use this when a specific address is unavailable.
                    </Typography>
                </Box>
            )}
        </Box>

        {/* What-you're-getting strip, between the hero and the reports table. */}
        <Box
            sx={{
                ...fadeUpSx(200),
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                columnGap: 5,
                rowGap: 1.5,
                mt: 3,
                mb: 1,
            }}
        >
            {DIFFERENTIATORS.map(({ icon, label }) => (
                <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ color: '#206FA5', display: 'flex' }}>{icon}</Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 500, color: INK }}>{label}</Typography>
                </Box>
            ))}
        </Box>
        </Box>
    )
}
